// @flow
import * as React from 'react';
// $$FlowFixMe
import { useState, useRef } from 'react';
import type { Answer } from 'models';
import { ArticleContainer } from './QAComponents';
import { stripWarningTags } from 'components/ContentSection/UserGeneratedTextContainer';
import { ErrorAlert, Alert } from 'components/alerts';
import {
  useMediaUpload,
  DEFAULT_MAX_MEDIA_ITEMS,
  MediaThumbnail,
  ExistingMediaThumbnail,
  CaptionTextarea,
  MediaUploadControl,
  serializeResponseMedia,
} from './MediaUpload';
import classnames from 'classnames';
import { AnswerTextarea } from './AnswerTextarea';
import { Map as ImmutableMap } from 'immutable';
import { nanoid } from 'lib/nanoid';

type Props = {
  ugc: Answer,
  isLoading: boolean,
  error: ?string,
  onSubmit: ({
    answer_media: Array<*>,
    deleted_media: Array<UgcId>,
    text: string,
  }) => void,
  allowMedia: boolean,
  onCancel: () => void,
};

export function EditAnswer(props: Props) {
  const { ugc, onSubmit, isLoading, error, onCancel, allowMedia } = props;
  const [deletedMedia, setDeletedMedia] = useState([]);
  const [answerText, setAnswerText] = useState(stripWarningTags(ugc.answer));
  const [updatedCaptions, setUpdatedCaptions] = useState(ImmutableMap());
  const existingMedia = ugc.getMedia();
  const maxItems =
    DEFAULT_MAX_MEDIA_ITEMS - existingMedia.size + deletedMedia.length;
  const {
    media: newMedia,
    captions: newCaptions,
    canPersistMedia,
    addFiles,
    uploadFile,
    setCaption,
    removeFile,
    clearFiles,
    isModalOpen,
    toggleModal,
  } = useMediaUpload(maxItems);
  const isOverMediaLimit = maxItems - newMedia.size < 0;
  const idRef = useRef(nanoid());

  function id(strings: string[]) {
    return `${idRef.current}-${strings.join('-')}`;
  }

  function toggleDeleteMedia(ugcId: Id) {
    setDeletedMedia(list => {
      if (list.includes(ugcId)) {
        return list.filter(i => i !== ugcId);
      }
      return [...list, ugcId];
    });
  }

  function getUgcCaption(ugc: *) {
    return updatedCaptions.has(ugc.ugc_id)
      ? updatedCaptions.get(ugc.ugc_id)
      : stripWarningTags(ugc.caption);
  }

  function serializeUgcToResponseMediaDTO(ugc: *) {
    return {
      ugc_id: ugc.ugc_id,
      ugc_type: ugc.ugctype,
      url: ugc.url,
      caption: getUgcCaption(ugc),
    };
  }

  function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      answer_media: [
        ...serializeResponseMedia(newMedia, newCaptions),
        ...existingMedia
          .filter(ugc => !deletedMedia.includes(ugc.ugc_id))
          .map(serializeUgcToResponseMediaDTO),
      ],
      deleted_media: deletedMedia,
      text: answerText,
    };
    onSubmit(payload);
  }

  return (
    <ArticleContainer
      ugctype="Answer"
      main={
        <form
          onSubmit={handleSubmit}
          style={{
            opacity: isLoading ? 0.5 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
          }}
        >
          {error != null ? <ErrorAlert>{error}</ErrorAlert> : null}
          <AnswerTextarea
            id={id`input`}
            value={answerText}
            onChange={e => setAnswerText(e.target.value)}
            placeholder="Add an answer to this question"
          />
          {isOverMediaLimit ? (
            <Alert
            >{`You can only include ${DEFAULT_MAX_MEDIA_ITEMS} media items.`}</Alert>
          ) : null}
          <ul className="list mv4 pa0">
            {existingMedia.map(ugc => {
              const ugcId = ugc.ugc_id;
              const isDeleted = deletedMedia.includes(ugc.ugc_id);

              return (
                <li
                  className={classnames('flex items-start mb4', {
                    'o-50': isDeleted,
                  })}
                  key={ugcId}
                >
                  <ExistingMediaThumbnail
                    ugc={ugc}
                    onDelete={toggleDeleteMedia}
                  />
                  <CaptionTextarea
                    value={getUgcCaption(ugc)}
                    placeholder="Add a caption"
                    disabled={isDeleted}
                    onChange={e => {
                      const caption = e.target.value;
                      setUpdatedCaptions(curr => curr.set(ugcId, caption));
                    }}
                  />
                </li>
              );
            })}
          </ul>
          <ul className="list mv4 pa0">
            {Array.from(newMedia.entries()).map(([id, item]: [string, *]) => {
              return (
                <li key={id} className="flex items-start mb4">
                  <MediaThumbnail item={item} onDelete={removeFile} />
                  <CaptionTextarea
                    placeholder="Add a caption..."
                    value={newCaptions.get(id) || ''}
                    onChange={e => {
                      setCaption(id, e.target.value);
                    }}
                  />
                </li>
              );
            })}
          </ul>
          <div className="flex">
            {allowMedia && (
              <MediaUploadControl
                media={newMedia}
                isOpen={isModalOpen}
                onToggle={toggleModal}
                onUpload={uploadFile}
                onDelete={removeFile}
                maxItems={maxItems}
                onAdd={addFiles}
                id={id`media-input`}
              />
            )}
            <div style={{ marginLeft: 'auto' }}>
              <button
                className="btn btn--small btn--link"
                type="reset"
                onClick={() => {
                  clearFiles();
                  onCancel();
                }}
              >
                Cancel
              </button>
              <button
                className={classnames('btn btn--small btn--primary', {
                  'btn--loading': isLoading,
                })}
                type="submit"
                disabled={
                  canPersistMedia === false || isOverMediaLimit === true
                }
              >
                {error == null ? 'Save' : 'Retry'}
              </button>
            </div>
          </div>
        </form>
      }
    />
  );
}



// WEBPACK FOOTER //
// ./src/components/ContentSection/__future__/EditAnswer.js